/**
 * C1 v1.8, editors from C1 v1.22 (item5): the Discounts tab reads (C2 v1.10 `GET /api/discounts/subscriptions/{market}`,
 * `GET /api/discounts/partners` and `GET /api/discounts/partners/net/{market}`), its writes (the same module's `PUT` and `DELETE`
 * routes) and every sentence of the tab. Types mirror C2's JSON field for field; a rate or a price is the API's string and is only
 * ever shown, never computed with (`format.ts` renders it). A percentage a person types becomes C2's fraction by string work alone
 * (`pctFraction`), as the Config boxes clean a number: nothing here reads a figure as a number.
 *
 * C1 v1.48 (c1-funnel-partners, G3): the Partners pane groups its programmes under the Distribution Funnel's partner types and
 * says, per type, what the funnel lists, from the Partners read's `funnel` block (C2 v1.49): counts per lead stage (most points are
 * identified leads, not partners: owner ruling 3), the sync date, the stale banner and the types no programme type takes.
 */
import { keepPreviousData, type QueryClient } from "@tanstack/react-query";

import { DEFAULT_BASIS, GRID_STALE_TIME, PricingError, type Basis as MarginBasis, type Channel, type Market } from "./api";
import { writeRefusal } from "./config";
import { COPY, MARKET_LABEL, stamp, STORE_LABEL } from "./format";
import type { Band } from "./kpi";
import type { RowBands } from "./signal";
import { cleanNote, VIEWS_KEY } from "./strategies";

export type Kind = "standard" | "max";
export type SubGrain = "lane" | "marketplace" | "store";
/** One resolved subscription rate as C2 serves it: the row it came from, and whether that row is the key asked. */
export type SubEntry = {
  rate: string; kind: Kind; marketplace_id: string; inherited: boolean; id: number; note: string | null; source: string;
  set_by: string; set_at: string;
};
/** One subscription row at the assignment grain: an Amazon SKU's lane (ALL) then its marketplaces; a Shopify SKU's store row. */
export type SubRow = {
  xero_sku: string; marketplace_id: string; marketplace_name: string | null; grain: SubGrain; product: string; variant: string;
  channel: Channel; channel_location: string; market: Market; standard: SubEntry | null; max: SubEntry | null; not_set: string | null;
};
export type Subscriptions = { market: Market; row_count: number; set_count: number; sku_count: number; set_sku_count: number; rows: SubRow[] };
/** A subscription write's answer: the row as the read serves it, plus `unchanged` on a put and `retired` on a delete. */
export type SubAnswer = SubRow & { unchanged?: boolean; retired?: number };

export type Basis = "unit" | "case";
export type Base = "shopify" | "amazon" | "rrp";
export type RateRow = {
  id: number; tracking: string; variant: string; market: string; rate: string | null; available: boolean; note: string | null;
  source: string; set_by: string; set_at: string;
};
/** One active partner programme: its default rate and terms, and its product rate rows by tracking, variant and market. */
export type Programme = {
  id: number; name: string; default_rate: string | null; basis: Basis; base: Base; volume_note: string | null; funnel_type: string | null;
  note: string | null; source: string; set_by: string; set_at: string; rates: RateRow[]; rate_count: number;
};
export type Vocabulary = { basis: string[]; base: string[]; funnel_type: string[]; market: string[]; kind: string[] };
/** What the Distribution Funnel lists, as C7 last read it (C2 v1.49): when C7 read it, the funnel's own instant, stale after 48
 * hours, the points in all, per partner type, per type and lead stage, and the funnel types no partner type takes. Counts only. */
export type Funnel = {
  synced_at: string; data_as_of: string; stale: boolean; partners: number; by_type: Record<string, number>;
  by_stage: Record<string, Record<string, number>>; unmapped: Record<string, number>;
};
/** `funnel` is null before C7's first read of the funnel (and absent from an answer older than C2 v1.49). */
export type Partners = { programmes: Programme[]; programme_count: number; vocabulary: Vocabulary; funnel?: Funnel | null };
/** A programme or rate write's answer: the programme as the read serves it, plus what the write did. A programme save that
 * changed something re-versions it under a new id (`previous_id` names the old one); a rate write keeps the programme's id. */
export type ProgrammeAnswer = Programme & { previous_id?: number | null; unchanged?: boolean; rate_id?: number; retired?: number };
export type RetireAnswer = { id: number; name: string; retired: number; rates_retired: number };

export type NetGrain = "store" | "lane" | "marketplace" | "wholesale";
/** The price a net price came off: the SKU whose committed price was read (null for an RRP) and the units it was multiplied by. */
export type BasePrice = { value: string; currency: string; as_at: string; source: string; sku: string | null; units: number };
export type PartnerFigures = {
  source: "projection" | "wholesale"; net_ex_vat: string | null; vat_rate: string | null; cogs: unknown;
  fulfilment_total: string | null;
  margins: { gross_pct: string | null; fulfilment_pct: string | null; cogs_fulfilment_pct: string | null; fees_pct: string | null } | null;
  missing: string[];
};
export type PartnerSummary = { worst_band: Band | null; below_good_count: number; banded_count: number; row_count: number };
/** One row of a programme's net-price matrix: the SKU, the rate that resolved for it, and the net price or the one reason. */
export type NetRow = {
  xero_sku: string; marketplace_id: string; marketplace_name: string | null; grain: NetGrain; product: string; variant: string;
  channel: string; channel_location: string; programme_id: number; programme: string; basis: Basis; base: Base; rate: string | null;
  available: boolean; resolved_from: string; rate_id: number | null; note: string | null; per: "unit" | "case";
  units_per_case: number | null; base_price: BasePrice | null; net_price: string | null; currency: string | null; not_priced: string | null;
  figures: PartnerFigures | null; bands: RowBands; net_per_unit: string | null;
};
export type NetProgramme = {
  id: number; name: string; basis: Basis; base: Base; default_rate: string | null; rows: NetRow[]; row_count: number; priced_count: number;
  summary: PartnerSummary;
};
export type NetPrices = { market: Market; basis: MarginBasis; programmes: NetProgramme[]; programme_count: number };
export type PartnerDraft =
  | { kind: "rate"; tracking: string; variant: string; market: string; rate: string | null; available: boolean }
  | { kind: "default"; rate: string; available: true };
export type PartnerPreview = {
  market: Market; basis: MarginBasis; programme: { id: number; name: string; basis: Basis; base: Base; default_rate: string | null };
  draft: PartnerDraft; rows: Array<{ before: NetRow; after: NetRow }>; moved_count: number; band_change_count: number;
  summary: { before: PartnerSummary; after: PartnerSummary }; written: false;
};

export const MARKETS: Market[] = ["UK", "EU", "AE"];
export const BASE_WORD: Record<Base, string> = { shopify: "Shopify", amazon: "Amazon", rrp: "RRP" };
export const BASIS_WORD: Record<Basis, string> = { unit: "per unit", case: "per case" };
/** The wholesale lanes have no store name in `format.ts`; the net matrix names them like the stores. */
export const WHOLESALE_LABEL: Record<string, string> = { wholesale_uk: "Wholesale UK", wholesale_eu: "Wholesale EU", wholesale_mena: "Wholesale UAE" };
export const laneLabel = (location: string) => STORE_LABEL[location] ?? WHOLESALE_LABEL[location] ?? location;
/** The funnel partner types of C2's vocabulary, in words; "none" is the form's empty choice, sent as null. */
export const FUNNEL_WORD: Record<string, string> = { starter: "Starter", wholesale: "Wholesale", ambassador: "Ambassador", distributor: "Distributor", affiliate: "Affiliate" };
export const NO_FUNNEL = "none";
/** A rate row's variant and market as the store keys them, and a subscription's lane or store row: ALL. */
export const ALL = "ALL";
export const variantWord = (variant: string) => (variant === ALL ? "All variants" : variant);
export const rateMarketWord = (market: string) => (market === ALL ? "All markets" : MARKET_LABEL[market as Market] ?? market);

export type DiscountPane = "subscriptions" | "partners";
export const DISCOUNT_PANES: DiscountPane[] = ["subscriptions", "partners"];
/** The pane in the URL (`pp_dpane`): Subscriptions is the default and writes nothing, as the Shopify Economics tab's panes. */
export const discountPane = (search: Record<string, unknown>): DiscountPane => (search.pp_dpane === "partners" ? "partners" : "subscriptions");
export const discountPaneSearch = (search: Record<string, unknown>, pane: DiscountPane): Record<string, unknown> =>
  ({ ...search, pp_dpane: pane === "subscriptions" ? undefined : pane });

export const DISCOUNTS_COPY = {
  title: "Discounts",
  desc: "Subscription discounts per product and lane, and the distribution partner programmes with the net price each leaves.",
  panes: { subscriptions: "Subscriptions", partners: "Distribution Partners" } as Record<DiscountPane, string>,
  retry: "Retry",
  unchanged: "No change: that is what is set already.",
  notSaved: "Not saved: the pricing API did not answer. Try again shortly.",
  subscriptions: {
    title: "Subscriptions",
    desc: "The subscription rate per product and channel-location lane (Amazon Subscribe and Save, Shopify Recharge), applied against the committed channel price. Click a rate to change it; an Amazon EU rate can differ per marketplace.",
    cannotLoad: "Cannot load subscriptions.",
    notRefreshed: "The subscriptions could not be refreshed, so this pane may be out of date. Retry before changing anything.",
    columns: { product: "Product" },
    off: (rate: string) => `${rate} off`,
    tiersDeferred: "Tiers not built. Flat rate per product and lane, shown below.",
    override: (marketplace: string, rate: string) => `${marketplace}: ${rate} off`,
    none: "—",
    noOffer: "No subscription offer",
    nothingSet: "Nothing defined yet. Click a cell to add a subscription discount to a product and channel.",
    summary: (market: string, setSkus: number, skus: number) => `${market}: ${setSkus} of ${skus} SKUs set`,
    note: "Discounts apply to subscription orders only. When the average discount cost arrives in the Costs log it leaves them out, so a subscription discount is never counted twice. The Price Run's Subscription view nets the standard rate.",
  },
  editor: {
    /** The cell's toggle: its visible text first (WCAG 2.5.3, label in name), then what it is. */
    cellLabel: (shown: string, product: string, place: string) => `${shown}, subscription rate of ${product} on ${place}`,
    rateLabel: (product: string, place: string) => `Subscription rate, ${product}, ${place}`,
    noteLabel: (product: string, place: string) => `Note, ${product}, ${place}`,
    pctOff: "% off",
    notePlaceholder: "Note (optional)",
    save: "Save",
    remove: "Remove",
    close: "Close",
    marketplaces: "Marketplaces",
    sameAsLane: "same as lane",
    enterPct: "Enter a percentage from 0 to 100, such as 10 or 12.5.",
    emptyOwn: (marketplace: boolean) =>
      marketplace ? "Enter a percentage, or Remove to take the lane's rate." : "Enter a percentage, or Remove to end the subscription offer.",
    setBy: (by: string, at: string, source: string) => (source === "observed_orders" ? `Seeded from observed orders by ${by} · ${at}` : `Set by ${by} · ${at}`),
    saved: (product: string, place: string, pct: string) => `Saved: ${product} on ${place}, ${pct}% off.`,
    removedLane: (product: string, place: string, kept: string[]) =>
      `Removed: ${product} on ${place} has no subscription offer now.${kept.length === 0 ? "" : kept.length === 1 ? ` ${kept[0]} keeps its own rate.` : ` ${kept.join(", ")} keep their own rates.`}`,
    removedPlace: (product: string, place: string, lane: string, laneRate: string | null) =>
      laneRate === null ? `Removed: ${product} on ${place} has no subscription offer now.` : `Removed: ${product} on ${place} takes the ${lane} rate now, ${laneRate} off.`,
  },
  partners: {
    title: "Distribution Partners",
    desc: "Pricing per partner programme and product: the rate and the net price it leaves, off the programme's base price, by one rule.",
    cannotLoad: "Cannot load distribution partners.",
    notRefreshed: "The partner programmes could not be refreshed, so this pane may be out of date. Retry before changing anything.",
    empty: "Nothing defined yet. Add a partner programme to begin.",
    add: "Add programme",
    edit: "Edit programme",
    retire: "Retire",
    columns: ["Product", "Lane", "Rate", "Net price"],
    rateColumns: ["Product", "Market", "Rate", "Note", ""],
    ratesTitle: "Product rates",
    netTitle: "Net price per SKU",
    terms: (basis: Basis, base: Base, rate: string | null) =>
      rate === null ? `${BASIS_WORD[basis]}, off the ${BASE_WORD[base]} price, no default rate` : `${rate} off ${BASIS_WORD[basis]}, off the ${BASE_WORD[base]} price`,
    setBy: (by: string, at: string, source: string) => (source === "legacy_sheet" ? `Imported from the legacy sheet by ${by} · ${at}` : `Set by ${by} · ${at}`),
    funnel: (type: string) => `Funnel type: ${FUNNEL_WORD[type] ?? type}`,
    rates: (n: number) => (n === 1 ? "1 product rate" : `${n} product rates`),
    noRates: (defaultRate: string | null) =>
      defaultRate === null ? "No product rate and no default rate: no product is priced yet." : `No product rate: every product takes the programme default, ${defaultRate} off.`,
    fromDefault: "programme default",
    fromRate: "product rate",
    notAvailable: "Not available",
    offBase: (units: number, sku: string, price: string) => (units === 1 ? `off ${sku} ${price}` : `${units} × ${sku} ${price}`),
    offRrp: (units: number, price: string) => (units === 1 ? `off the RRP ${price}` : `${units} × the RRP ${price}`),
    noRows: "No product this programme can price in this market yet.",
    note: "Net price is off the committed price of the programme's base; a case programme prices the case SKU from its retail sibling times the case units. Price Run will show the same net price in each partner view and never pushes it.",
    group: {
      noType: "No partner type",
      noTypeHint: "These programmes have no Distribution Funnel partner type. Set one with Edit programme to place each under its type.",
      inFunnel: (total: number, stages: string) => (stages ? `${total} in the Distribution Funnel: ${stages}` : `${total} in the Distribution Funnel`),
      noneInFunnel: "None in the Distribution Funnel",
      newInFunnel: "New in the Distribution Funnel: no programme or rates yet. Add a programme with this partner type to price it.",
      noProgramme: "No programme yet.",
    },
    noRateSet: "No rate set",
    stale: (date: string) => `The Distribution Funnel type list is unreadable. Showing the last synced list from ${date}.`,
    synced: (date: string) => `Partner types last synced from the Distribution Funnel ${date}.`,
    notSynced: "Partner types have not been synced from the Distribution Funnel yet.",
    unmapped: (total: number, parts: string) => `${total === 1 ? "1 funnel point maps" : `${total} funnel points map`} to no partner type: ${parts}.`,
    form: {
      createTitle: "New partner programme",
      editTitle: (name: string) => `Edit ${name}`,
      name: "Name",
      defaultRate: "Default rate",
      defaultHint: "Leave blank for no default rate: then only the products with a rate of their own are priced.",
      basis: "Priced",
      pickBasis: "Per unit or per case",
      funnel: "Funnel type",
      noFunnel: "None",
      volumeNote: "Volume note",
      note: "Note",
      optional: "optional",
      base: (base: Base) => `Off the ${BASE_WORD[base]} price.`,
      create: "Add programme",
      save: "Save programme",
      cancel: "Cancel",
      nameRequired: "Enter a name for the programme.",
      nameTaken: (name: string) => `A programme named ${name} exists already; edit it instead.`,
      basisRequired: "Choose per unit or per case.",
    },
    rate: {
      product: "Product",
      variant: "Variant",
      market: "Market",
      pickProduct: "Choose a product",
      notAvailable: "Not available",
      add: "Add rate",
      save: "Save",
      remove: "Remove",
      productRequired: "Choose a product first.",
      taken: "That product, variant and market has a rate already; change it in its row.",
      label: (programme: string, product: string, market: string) => `Rate, ${programme}, ${product}, ${market}`,
      noteLabel: (programme: string, product: string, market: string) => `Note, ${programme}, ${product}, ${market}`,
      availableLabel: (programme: string, product: string, market: string) => `Not available, ${programme}, ${product}, ${market}`,
    },
    retireDialog: {
      title: (name: string) => `Retire ${name}?`,
      body: (n: number) =>
        `${n === 0 ? "It has no product rate." : n === 1 ? "Its 1 product rate retires with it." : `Its ${n} product rates retire with it.`} The history stays, and its net prices stop at once. To use it again, add the programme again.`,
      cancel: "Cancel",
      confirm: "Retire programme",
    },
    done: {
      added: (name: string) => `Added ${name}.`,
      saved: (name: string) => `Saved ${name}.`,
      retired: (name: string, n: number) => `Retired ${name}${n === 0 ? "" : n === 1 ? " and its 1 product rate" : ` and its ${n} product rates`}.`,
      rateSaved: (name: string, product: string, market: string, pct: string | null) =>
        pct === null ? `Saved: ${name}, ${product} in ${market}, not available.` : `Saved: ${name}, ${product} in ${market}, ${pct}% off.`,
      rateRemoved: (name: string, product: string, market: string, defaultRate: string | null) =>
        defaultRate === null ? `Removed: ${name}, ${product} in ${market} has no rate now, so it is not priced.` : `Removed: ${name}, ${product} in ${market} takes the programme default now, ${defaultRate} off.`,
    },
  },
};

const BASE = import.meta.env.VITE_PRICING_API_BASE ?? "/precision_pricing/api";
const HEADERS = { Accept: "application/json" };
const JSON_HEADERS = { Accept: "application/json", "Content-Type": "application/json" };
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Same shape as api.ts `call`: the cookie rides on credentials; no-store so a save elsewhere shows on the next read. A write with a
  // body sends JSON and says so; a read or a delete sends only Accept, as the reads did before the editors.
  const headers = init.body === undefined ? HEADERS : JSON_HEADERS;
  const response = await fetch(`${BASE}${path}`, { method: "GET", credentials: "include", cache: "no-store", headers, ...init });
  if (!response.ok) {
    let body: { error?: { reason?: string; message?: string } } | null = null;
    try { body = await response.json() as typeof body; } catch { body = null; }
    throw new PricingError(response.status, body);
  }
  return await response.json() as T;
}
export const getSubscriptions = (market: Market) => call<Subscriptions>(`/discounts/subscriptions/${market}`);
export const getPartners = () => call<Partners>("/discounts/partners");
export const getNetPrices = (market: Market, basis: MarginBasis = DEFAULT_BASIS) =>
  call<NetPrices>(`/discounts/partners/net/${market}?${new URLSearchParams({ basis })}`);
export const getPartnerPreview = (programmeId: number, market: Market, basis: MarginBasis, draft: PartnerDraft) => {
  const params = new URLSearchParams({ market, basis });
  if (draft.kind === "rate") {
    params.set("tracking", draft.tracking);
    params.set("variant", draft.variant);
    params.set("rate_market", draft.market);
    if (draft.available) params.set("rate", draft.rate ?? ""); else params.set("available", "false");
  } else {
    params.set("default_rate", draft.rate);
  }
  return call<PartnerPreview>(`/discounts/partners/${programmeId}/preview?${params}`);
};

/** The body C2's `PUT /api/discounts/subscriptions/{xero_sku}` takes: always the standard kind (the 2026-09-27 flat-rate ruling: no tiers). */
export type SubBody = { marketplace_id: string; kind: "standard"; rate: string; note: string | null };
export type RateKey = { tracking: string; variant: string; market: string };
export type RateBody = RateKey & { rate: string | null; available: boolean; note: string | null };
export type ProgrammeBody = {
  name: string; default_rate: string | null; basis: Basis; base: Base; volume_note: string | null; funnel_type: string | null; note: string | null;
};
const skuPath = (xeroSku: string) => encodeURIComponent(xeroSku);
const put = (body: unknown): RequestInit => ({ method: "PUT", body: JSON.stringify(body) });
const DELETE: RequestInit = { method: "DELETE" };
export const putSubscription = (xeroSku: string, body: SubBody) => call<SubAnswer>(`/discounts/subscriptions/${skuPath(xeroSku)}`, put(body));
export const deleteSubscription = (xeroSku: string, marketplaceId: string) =>
  call<SubAnswer>(`/discounts/subscriptions/${skuPath(xeroSku)}?${new URLSearchParams({ marketplace_id: marketplaceId, kind: "standard" })}`, DELETE);
export const putProgramme = (body: ProgrammeBody) => call<ProgrammeAnswer>("/discounts/partners", put(body));
export const retireProgramme = (programmeId: number) => call<RetireAnswer>(`/discounts/partners/${programmeId}`, DELETE);
export const putRate = (programmeId: number, body: RateBody) => call<ProgrammeAnswer>(`/discounts/partners/${programmeId}/rates`, put(body));
export const deleteRate = (programmeId: number, key: RateKey) =>
  call<ProgrammeAnswer>(`/discounts/partners/${programmeId}/rates?${new URLSearchParams({ tracking: key.tracking, variant: key.variant, market: key.market })}`, DELETE);

export const SUBSCRIPTIONS_KEY = (market: Market) => ["pricing", "discounts", "subscriptions", market] as const;
export const PARTNERS_KEY = ["pricing", "discounts", "partners"] as const;
export const NET_KEY = (market: Market, basis: MarginBasis = DEFAULT_BASIS) => ["pricing", "discounts", "partners", "net", market, basis] as const;
/** Reused for the grid's stale window: a rate changes when a person saves it, and the tab refetches on focus. */
export function subscriptionsQuery(market: Market) {
  return { queryKey: SUBSCRIPTIONS_KEY(market), queryFn: () => getSubscriptions(market), retry: 1, staleTime: GRID_STALE_TIME };
}
export function partnersQuery() {
  return { queryKey: PARTNERS_KEY, queryFn: getPartners, retry: 1, staleTime: GRID_STALE_TIME };
}
export function netPricesQuery(market: Market, basis: MarginBasis = DEFAULT_BASIS) {
  return {
    queryKey: NET_KEY(market, basis), queryFn: () => getNetPrices(market, basis), retry: 1, staleTime: GRID_STALE_TIME,
    placeholderData: keepPreviousData,
  };
}
export function partnerPreviewQuery(programmeId: number, market: Market, basis: MarginBasis, draft: PartnerDraft, changed: boolean) {
  const valid = draft.kind === "default" ? draft.rate !== "" : (!draft.available || draft.rate !== null);
  return {
    queryKey: ["pricing", "discounts", "partners", "preview", programmeId, market, basis, draft] as const,
    queryFn: () => getPartnerPreview(programmeId, market, basis, draft),
    enabled: changed && valid,
    retry: 1,
    staleTime: 0,
  };
}
/** A refused discount write in words: the store's or C2's sentence, as every Config write (`writeRefusal`), except that a write the
 * API never answered says nothing was saved (Config's unreachable line, "so nothing is shown", is a read's sentence). */
export function discountRefusal(error: unknown, areaName: string): string {
  const text = writeRefusal(error, areaName);
  return text === COPY.unreachable ? DISCOUNTS_COPY.notSaved : text;
}
/** After a subscription write: the three market reads (one prefix) and the Price Run's order-type views (C1 v1.23), which net
 * the rates. */
export const refreshSubscriptions = async (client: QueryClient): Promise<void> => {
  await Promise.all([client.invalidateQueries({ queryKey: ["pricing", "discounts", "subscriptions"] }), client.invalidateQueries({ queryKey: VIEWS_KEY })]);
};
/** After a programme or rate write: the programmes and every market's net prices (the partners key is the net keys' prefix),
 * and the order-type views. */
export const refreshPartners = async (client: QueryClient): Promise<void> => {
  await Promise.all([
    client.invalidateQueries({ queryKey: PARTNERS_KEY }),
    client.invalidateQueries({ queryKey: ["pricing", "discounts", "partners", "preview"] }),
    client.invalidateQueries({ queryKey: VIEWS_KEY }),
  ]);
};

/**
 * A percentage a person typed ("10", "12.5", "10,5") as the fraction C2 stores ("0.1", "0.125"), or null when it is not a number
 * from 0 to 100 with at most four decimals (C2 keeps six decimals of the fraction). The point moves two places by string work alone.
 */
export function pctFraction(raw: string): string | null {
  const match = /^(\d{1,3})(?:\.(\d{1,4}))?$/.exec(raw.trim().replace(",", "."));
  if (!match) return null;
  const whole = match[1].replace(/^0+(?=\d)/, "");
  const decimals = match[2] ?? "";
  if (whole.length === 3 && (whole !== "100" || /[1-9]/.test(decimals))) return null; // above 100
  const padded = whole.padStart(3, "0");
  const head = padded.slice(0, -2).replace(/^0+(?=\d)/, "");
  const tail = (padded.slice(-2) + decimals).replace(/0+$/, "");
  return tail ? `${head}.${tail}` : head;
}
/** C2's fraction ("0.125") as the percentage an input shows ("12.5"): `pctFraction` backwards, by string work alone. */
export function fractionPct(rate: string): string {
  const [whole, decimals = ""] = rate.split(".");
  const digits = decimals.padEnd(2, "0");
  const head = `${whole}${digits.slice(0, 2)}`.replace(/^0+(?=\d)/, "");
  const tail = digits.slice(2).replace(/0+$/, "");
  return tail ? `${head}.${tail}` : head;
}

/** A lane of the matrix: "Amazon UK", "Shopify EU", keyed by the channel location so the columns read as the approved design's. */
export type Lane = { key: string; label: string; market: Market };
/** A marketplace of an Amazon lane in the cell editor: its own standard row, or null while it takes the lane's rate. */
export type SubSlot = { marketplace_id: string; marketplace_name: string; own: SubEntry | null };
/**
 * One cell of the Subscriptions matrix: the SKU it writes to, the lane's standard rate and its own entry (what the editor starts
 * from), its own max rate when one is set, the marketplace overrides, and the marketplaces the editor offers: every marketplace of an
 * Amazon lane of several; on a one-marketplace lane (Amazon UK, Amazon UAE) the rate is the lane's, so only a leftover own row shows,
 * to be removed.
 */
export type SubCell = {
  sku: string; product: string; laneKey: string; laneLabel: string; market: Market;
  standard: string | null; own: SubEntry | null; max: string | null; notSet: string | null;
  overrides: Array<{ marketplace: string; rate: string }>; slots: SubSlot[];
};
export type SubTable = { lanes: Lane[]; products: string[]; cells: Record<string, SubCell> };
export const cellKey = (lane: string, product: string) => `${lane}|${product}`;
export const productKey = (row: { product: string; variant: string }) => `${row.product} · ${row.variant}`;
/**
 * Folds the per-market reads into the design's table: products down, lanes across. The lane or store row (`ALL`) names the cell and
 * its SKU (the registry holds one active retail SKU per lane, product and variant); its `max` shows only when a max row of its own
 * exists (C2 resolves `max` to the standard row otherwise, so the entry's `kind` says which); a marketplace row whose standard rate is
 * its own (`inherited` false) is an override under it.
 */
export function subscriptionTable(reads: Subscriptions[]): SubTable {
  const lanes: Lane[] = []; const products: string[] = []; const cells: Record<string, SubCell> = {};
  const places: Record<string, SubSlot[]> = {};
  for (const read of reads) {
    for (const row of read.rows) {
      const laneKey = row.channel_location;
      if (!lanes.some((l) => l.key === laneKey)) lanes.push({ key: laneKey, label: laneLabel(laneKey), market: read.market });
      const product = productKey(row);
      if (!products.includes(product)) products.push(product);
      const key = cellKey(laneKey, product);
      const cell = (cells[key] ??= {
        sku: row.xero_sku, product, laneKey, laneLabel: laneLabel(laneKey), market: read.market,
        standard: null, own: null, max: null, notSet: null, overrides: [], slots: [],
      });
      const own = row.standard && !row.standard.inherited ? row.standard : null;
      if (row.grain !== "marketplace") {
        cell.sku = row.xero_sku;
        cell.standard = row.standard?.rate ?? null;
        cell.own = own;
        cell.max = row.max && row.max.kind === "max" ? row.max.rate : null;
        cell.notSet = row.not_set;
      } else {
        if (own) cell.overrides.push({ marketplace: row.marketplace_name ?? row.marketplace_id, rate: own.rate });
        (places[key] ??= []).push({ marketplace_id: row.marketplace_id, marketplace_name: row.marketplace_name ?? row.marketplace_id, own });
      }
    }
  }
  for (const [key, slots] of Object.entries(places)) cells[key].slots = slots.length > 1 ? slots : slots.filter((s) => s.own);
  lanes.sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)); // code-point order: no locale in the fold
  return { lanes, products, cells };
}

/** What a cell editor asks C2 to do: set a rate on the lane or store row (`ALL`) or on a marketplace, or remove one. */
export type SubSend =
  | { kind: "put"; sku: string; body: SubBody; place: string }
  | { kind: "delete"; sku: string; marketplace_id: string; place: string };
export type Checked<T> = { ok: true; value: T } | { ok: false; error: string };
/** The note box of the lane or store row starts from the row's own note, except the seed's (a person's rate replaces the seed). */
export const noteStart = (own: SubEntry | null) => (own && own.source !== "observed_orders" ? own.note ?? "" : "");
/** The write a Save asks for, or the sentence that stops it. A marketplace keeps its own row's note (it has no note box); the lane
 * or store row sends what its note box says, blank as a clear. */
export function subscriptionSave(cell: SubCell, slot: SubSlot | null, pctRaw: string, noteRaw: string | null): Checked<SubSend> {
  const rate = pctFraction(pctRaw);
  if (rate === null) {
    // an emptied box on a row with its own rate names the way back: Remove (round 1, comment 7)
    const own = slot ? slot.own : cell.own;
    return { ok: false, error: own && pctRaw.trim() === "" ? DISCOUNTS_COPY.editor.emptyOwn(slot !== null) : DISCOUNTS_COPY.editor.enterPct };
  }
  const note = slot ? slot.own?.note ?? null : cleanNote(noteRaw);
  const body: SubBody = { marketplace_id: slot ? slot.marketplace_id : ALL, kind: "standard", rate, note };
  return { ok: true, value: { kind: "put", sku: cell.sku, body, place: slot ? slot.marketplace_name : cell.laneLabel } };
}
export function subscriptionRemove(cell: SubCell, slot: SubSlot | null): SubSend {
  return { kind: "delete", sku: cell.sku, marketplace_id: slot ? slot.marketplace_id : ALL, place: slot ? slot.marketplace_name : cell.laneLabel };
}
/** Whether the boxes still say what is set, so Save has nothing to send. A box that does not parse is a change, to be refused on Save.
 * A marketplace row has no note box (`noteRaw` null). */
export function subscriptionUnchanged(own: SubEntry | null, pctRaw: string, noteRaw: string | null): boolean {
  if (!own) return pctRaw.trim() === "";
  return pctFraction(pctRaw) === own.rate && (noteRaw === null || cleanNote(noteRaw) === cleanNote(noteStart(own)));
}
export const sendSubscription = (send: SubSend) => (send.kind === "put" ? putSubscription(send.sku, send.body) : deleteSubscription(send.sku, send.marketplace_id));
/** The result line of a subscription write, in the cell's words: C2's no-op, the rate saved, or what the cell takes now. */
export function subscriptionDone(cell: SubCell, send: SubSend, answer: SubAnswer): string {
  const E = DISCOUNTS_COPY.editor;
  if (send.kind === "put") return answer.unchanged ? DISCOUNTS_COPY.unchanged : E.saved(cell.product, send.place, fractionPct(send.body.rate));
  if (send.marketplace_id === ALL) return E.removedLane(cell.product, send.place, cell.overrides.map((o) => o.marketplace));
  const lane = answer.standard ? `${fractionPct(answer.standard.rate)}%` : null; // the marketplace's own row is gone: C2 answers the lane's
  return E.removedPlace(cell.product, send.place, cell.laneLabel, lane);
}

/** The programme form's boxes, as typed. */
export type ProgrammeDraft = { name: string; default_rate: string; basis: Basis | ""; funnel_type: string; volume_note: string; note: string };
export function programmeDraft(programme: Programme | null): ProgrammeDraft {
  if (!programme) return { name: "", default_rate: "", basis: "", funnel_type: NO_FUNNEL, volume_note: "", note: "" };
  return {
    name: programme.name, default_rate: programme.default_rate === null ? "" : fractionPct(programme.default_rate), basis: programme.basis,
    funnel_type: programme.funnel_type ?? NO_FUNNEL, volume_note: programme.volume_note ?? "", note: programme.note ?? "",
  };
}
/**
 * The body a programme save sends, or the sentence that stops it. A new programme comes off the Shopify price (owner ruling 2026-09-27); an edit
 * keeps its name (the store keys a programme by name) and its base. A new name the tab already lists, in any case, is refused here:
 * C2 would re-version that programme instead of adding one.
 */
export function programmeBody(draft: ProgrammeDraft, existing: Programme | null, programmes: Programme[]): Checked<ProgrammeBody> {
  const F = DISCOUNTS_COPY.partners.form;
  const name = existing ? existing.name : draft.name.trim();
  if (!name) return { ok: false, error: F.nameRequired };
  if (!existing && programmes.some((p) => p.name.toLowerCase() === name.toLowerCase())) return { ok: false, error: F.nameTaken(name) };
  const typed = draft.default_rate.trim();
  const defaultRate = typed === "" ? null : pctFraction(typed);
  if (typed !== "" && defaultRate === null) return { ok: false, error: DISCOUNTS_COPY.editor.enterPct };
  if (draft.basis === "") return { ok: false, error: F.basisRequired };
  return {
    ok: true,
    value: {
      name, default_rate: defaultRate, basis: draft.basis, base: existing ? existing.base : "shopify",
      volume_note: cleanNote(draft.volume_note), funnel_type: draft.funnel_type === NO_FUNNEL ? null : draft.funnel_type, note: cleanNote(draft.note),
    },
  };
}
export function programmeDone(existing: Programme | null, answer: ProgrammeAnswer): string {
  if (answer.unchanged) return DISCOUNTS_COPY.unchanged;
  return existing ? DISCOUNTS_COPY.partners.done.saved(answer.name) : DISCOUNTS_COPY.partners.done.added(answer.name);
}

/** The products a programme can price, from its net rows in every market: each product with its variants, in code-point order. */
export type ProductOption = { tracking: string; variants: string[] };
export function productOptions(rows: Array<Pick<NetRow, "product" | "variant">>): ProductOption[] {
  const byProduct = new Map<string, Set<string>>();
  for (const row of rows) {
    const variants = byProduct.get(row.product) ?? new Set<string>();
    variants.add(row.variant);
    byProduct.set(row.product, variants);
  }
  const order = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  return [...byProduct.entries()].sort(([a], [b]) => order(a, b)).map(([tracking, variants]) => ({ tracking, variants: [...variants].sort(order) }));
}
/** A rate row's boxes, as typed; a new row starts on every variant and every market. */
export type RateDraft = { tracking: string; variant: string; market: string; rate: string; available: boolean; note: string };
export function rateDraft(row: RateRow | null): RateDraft {
  if (!row) return { tracking: "", variant: ALL, market: ALL, rate: "", available: true, note: "" };
  return { tracking: row.tracking, variant: row.variant, market: row.market, rate: row.rate === null ? "" : fractionPct(row.rate), available: row.available, note: row.note ?? "" };
}
/** The body a rate save sends, or the sentence that stops it: a rate on an available row, none on a Not available one (C2 refuses
 * either the other way); a new row whose key the programme already has is refused here, since C2 would overwrite that row. */
export function rateBody(draft: RateDraft, programme: Programme, isNew: boolean): Checked<RateBody> {
  const R = DISCOUNTS_COPY.partners.rate;
  if (!draft.tracking) return { ok: false, error: R.productRequired };
  if (isNew && programme.rates.some((r) => r.tracking === draft.tracking && r.variant === draft.variant && r.market === draft.market)) {
    return { ok: false, error: R.taken };
  }
  const rate = draft.available ? pctFraction(draft.rate) : null;
  if (draft.available && rate === null) return { ok: false, error: DISCOUNTS_COPY.editor.enterPct };
  return { ok: true, value: { tracking: draft.tracking, variant: draft.variant, market: draft.market, rate, available: draft.available, note: cleanNote(draft.note) } };
}
/** Whether a rate row's boxes still say what is set, so Save has nothing to send. */
export function rateUnchanged(row: RateRow, draft: RateDraft): boolean {
  return draft.available === row.available && (!draft.available || pctFraction(draft.rate) === row.rate) && cleanNote(draft.note) === (row.note ?? null);
}
export const rateLabel = (key: RateKey) => `${key.tracking} · ${variantWord(key.variant)}`;
export function rateDone(programme: Programme, body: RateBody, answer: ProgrammeAnswer): string {
  if (answer.unchanged) return DISCOUNTS_COPY.unchanged;
  return DISCOUNTS_COPY.partners.done.rateSaved(programme.name, rateLabel(body), rateMarketWord(body.market), body.rate === null ? null : fractionPct(body.rate));
}
export function rateRemovedDone(programme: Programme, key: RateKey): string {
  const fallback = programme.default_rate === null ? null : `${fractionPct(programme.default_rate)}%`;
  return DISCOUNTS_COPY.partners.done.rateRemoved(programme.name, rateLabel(key), rateMarketWord(key.market), fallback);
}

/** "Amazon EU · Amazon.pl" for a marketplace row, "Shopify UK" for a store row, "Wholesale UK" for a case row. */
export function netLane(row: NetRow): string {
  const lane = laneLabel(row.channel_location);
  return row.grain === "marketplace" ? `${lane} · ${row.marketplace_name ?? row.marketplace_id}` : lane;
}
/** The cell's hint under a net price: which price it came off, times how many units; null while the row is not priced. */
export function baseHint(row: NetRow, price: (value: string, currency: string) => string): string | null {
  if (!row.base_price) return null;
  const shown = price(row.base_price.value, row.base_price.currency);
  return row.base_price.sku ? DISCOUNTS_COPY.partners.offBase(row.base_price.units, row.base_price.sku, shown) : DISCOUNTS_COPY.partners.offRrp(row.base_price.units, shown);
}
/** The rows of every market under their programme, in the markets' order then the API's, so a card lists UK, EU, AE together. */
export function netRowsByProgramme(reads: NetPrices[]): Record<number, Array<NetRow & { market: Market }>> {
  const out: Record<number, Array<NetRow & { market: Market }>> = {};
  for (const read of reads) {
    for (const programme of read.programmes) {
      const rows = (out[programme.id] ??= []);
      for (const row of programme.rows) rows.push({ ...row, market: read.market });
    }
  }
  return out;
}

/** Merge C2's per-market programme summaries. Counts add; the first band in C2's worst-to-best order wins. */
export function partnerSummary(summaries: PartnerSummary[]): PartnerSummary {
  const order: Array<Band> = ["Critical", "@ Risk", "Good", "Excellent", "Outstanding"];
  const bands = summaries.flatMap((summary) => summary.worst_band === null ? [] : [summary.worst_band]);
  return {
    worst_band: bands.sort((a, b) => order.indexOf(a) - order.indexOf(b))[0] ?? null,
    below_good_count: summaries.reduce((total, summary) => total + summary.below_good_count, 0),
    banded_count: summaries.reduce((total, summary) => total + summary.banded_count, 0),
    row_count: summaries.reduce((total, summary) => total + summary.row_count, 0),
  };
}

/** v26's order of the partner types (PARTNER_TYPES); a type C2's vocabulary adds later follows them, as spelled. */
export const PARTNER_TYPE_ORDER = ["wholesale", "starter", "ambassador", "distributor", "affiliate"];
/** A funnel lead stage's count in words, live first: most points are identified leads, not partners (owner ruling 3). */
export const STAGE_ORDER = ["live", "contract", "application", "identified"];
const STAGE_WORD: Record<string, (n: number) => string> = {
  live: (n) => `${n} live`, contract: (n) => `${n} in contract`, application: (n) => `${n} applying`,
  identified: (n) => (n === 1 ? "1 identified lead" : `${n} identified leads`), none: (n) => `${n} with no stage`,
};
export function stageWords(stages: Record<string, number>): string {
  const other = Object.keys(stages).filter((s) => !STAGE_ORDER.includes(s) && s !== "none").sort();
  const order = [...STAGE_ORDER, ...other, "none"].filter((s) => stages[s] > 0);
  return order.map((s) => (STAGE_WORD[s] ?? ((n: number) => `${n} ${s}`))(stages[s])).join(", ");
}
/** The funnel types no partner type takes, largest first ("none" is a point with no type). */
export function unmappedWords(unmapped: Record<string, number>): string {
  return Object.entries(unmapped).filter(([, n]) => n > 0).sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .map(([type, n]) => `${type === "none" ? "no type" : type} ${n}`).join(", ");
}
/** One partner type's group: its programmes and what the funnel lists for it (count null before the funnel's first read).
 * `type` null is the last group, the programmes with no partner type of the vocabulary. */
export type PartnerGroup = { type: string | null; programmes: Programme[]; count: number | null; stages: Record<string, number> };
export function partnerGroups(data: Partners): PartnerGroup[] {
  const vocab = data.vocabulary.funnel_type;
  const types = [...PARTNER_TYPE_ORDER.filter((t) => vocab.includes(t)), ...vocab.filter((t) => !PARTNER_TYPE_ORDER.includes(t))];
  const funnel = data.funnel ?? null;
  const groups: PartnerGroup[] = types.map((type) => ({
    type, programmes: data.programmes.filter((p) => p.funnel_type === type),
    count: funnel ? funnel.by_type[type] ?? 0 : null, stages: funnel?.by_stage[type] ?? {},
  }));
  const untyped = data.programmes.filter((p) => p.funnel_type === null || !types.includes(p.funnel_type));
  return untyped.length > 0 ? [...groups, { type: null, programmes: untyped, count: null, stages: {} }] : groups;
}
/** The group's line of what the funnel lists; null for the no-type group and before the funnel's first read. */
export function groupFunnelLine(group: PartnerGroup): string | null {
  const G = DISCOUNTS_COPY.partners.group;
  if (group.count === null) return null;
  return group.count === 0 ? G.noneInFunnel : G.inFunnel(group.count, stageWords(group.stages));
}
/** A type with no programme: new in the funnel when the funnel lists points of it, else no programme yet. */
export function groupEmptyNote(group: PartnerGroup): string | null {
  const G = DISCOUNTS_COPY.partners.group;
  if (group.programmes.length > 0 || group.type === null) return null;
  return group.count !== null && group.count > 0 ? G.newInFunnel : G.noProgramme;
}
/** The footnote's funnel lines: the sync date (or not synced yet) and the funnel types no partner type takes. */
export function funnelNotes(funnel: Funnel | null | undefined): string[] {
  const P = DISCOUNTS_COPY.partners;
  if (!funnel) return [P.notSynced];
  const total = Object.values(funnel.unmapped).reduce((a, n) => a + n, 0);
  return total > 0 ? [P.synced(stamp(funnel.synced_at)), P.unmapped(total, unmappedWords(funnel.unmapped))] : [P.synced(stamp(funnel.synced_at))];
}
/** The stale banner's sentence while the funnel's list is over 48 hours old (C2's rule); null otherwise. */
export function funnelStale(funnel: Funnel | null | undefined): string | null {
  return funnel?.stale ? DISCOUNTS_COPY.partners.stale(stamp(funnel.synced_at)) : null;
}
/** No rate set: neither a product rate nor a default resolved (C3 answers it as not available, with no rate row behind it), as
 * against a rate row ticked Not available. */
export const noRateSet = (row: NetRow) => row.rate === null && row.rate_id === null;
